import { useState, useEffect, useRef } from "react";
import { api, API } from "@/lib/api";
import { imgUrl } from "@/lib/imageUrl";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Edit, Trash2, Upload, Download, ChevronUp, ChevronDown, Image as ImageIcon, Search, PackageX } from "lucide-react";
import { toast } from "sonner";

export default function ProductsPanel() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [addons, setAddons] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editingType, setEditingType] = useState(null); // 'product' | 'category' | 'addon'
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const load = async () => {
    try {
      const [p, c, a] = await Promise.all([
        api.get("/admin/products"), api.get("/admin/categories"), api.get("/admin/addons"),
      ]);
      setProducts(p.data); setCategories(c.data); setAddons(a.data);
    } catch { toast.error("Erreur de chargement"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editingType === "product") {
        const payload = { ...editing };
        delete payload.id;
        if (editing.id) await api.put(`/admin/products/${editing.id}`, payload);
        else await api.post("/admin/products", payload);
      } else if (editingType === "category") {
        const payload = { name: editing.name, order: Number(editing.order || 0) };
        if (editing.id) await api.put(`/admin/categories/${editing.id}`, payload);
        else await api.post("/admin/categories", payload);
      } else if (editingType === "addon") {
        const payload = {
          name: editing.name,
          price: Number(editing.price || 0),
          required: !!editing.required,
          multi: editing.multi !== false,
          choices: (editing.choices || []).map(c => ({ id: c.id, name: c.name, price: Number(c.price || 0) })),
        };
        if (editing.id) await api.put(`/admin/addons/${editing.id}`, payload);
        else await api.post("/admin/addons", payload);
      }
      toast.success("Enregistré");
      setEditing(null); setEditingType(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const del = async (type, id) => {
    if (!window.confirm("Supprimer ?")) return;
    await api.delete(`/admin/${type}/${id}`);
    toast.success("Supprimé"); load();
  };

  return (
    <div data-testid="products-panel">
      <h1 className="font-display text-4xl mb-6">Gestion menu</h1>
      <Tabs defaultValue="products">
        <TabsList>
          <TabsTrigger value="products">Produits</TabsTrigger>
          <TabsTrigger value="categories">Catégories</TabsTrigger>
          <TabsTrigger value="addons">Suppléments</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="mt-6">
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => { setEditing({ name: "", description: "", price: 0, image: "", category_id: categories[0]?.id || "", available: true, addon_ids: [] }); setEditingType("product"); }}
              data-testid="add-product-btn"
              className="bg-primary text-white px-4 py-2 rounded-sm flex items-center gap-2 hover:bg-primary/90"
            ><Plus size={16} /> Ajouter un produit</button>

            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              data-testid="import-csv-btn"
              className="border border-primary text-primary px-4 py-2 rounded-sm flex items-center gap-2 hover:bg-primary hover:text-white transition disabled:opacity-50"
            ><Upload size={16} /> {importing ? "Import en cours…" : "Importer un CSV"}</button>

            <a
              href={`data:text/csv;charset=utf-8,${encodeURIComponent("nom,prix,categorie,description,image\nTonkotsu Ramen,22.50,Ramens Signature,Bouillon de porc 14h crémeux,https://exemple.com/image.jpg\nGyozas Porc,9.50,Gyozas & Bao,6 pièces grillées,\n")}`}
              download="modele-produits.csv"
              data-testid="download-csv-template"
              className="text-muted-foreground hover:text-primary text-sm flex items-center gap-1 px-3 py-2"
            ><Download size={14} /> Modèle CSV</a>

            <button
              type="button"
              disabled={importing}
              data-testid="migrate-images-btn"
              onClick={async () => {
                if (!window.confirm("Télécharger toutes les images externes et les héberger sur votre backend ?")) return;
                setImporting(true);
                try {
                  const { data } = await api.post("/admin/products/migrate-images");
                  toast.success(`${data.migrated} image${data.migrated > 1 ? "s" : ""} migrée${data.migrated > 1 ? "s" : ""}.${data.errors?.length ? ` (${data.errors.length} erreur(s))` : ""}`);
                  if (data.errors?.length) console.warn(data.errors);
                  load();
                } catch (e) { toast.error("Erreur de migration"); }
                finally { setImporting(false); }
              }}
              className="border border-primary text-primary px-4 py-2 rounded-sm text-sm flex items-center gap-2 hover:bg-primary hover:text-white transition disabled:opacity-50"
            ><Upload size={14} /> Héberger les images externes</button>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              data-testid="csv-file-input"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setImporting(true);
                try {
                  const fd = new FormData();
                  fd.append("file", file);
                  const token = localStorage.getItem("hana_admin_token");
                  const res = await fetch(`${API}/admin/products/import`, {
                    method: "POST",
                    headers: { Authorization: `Bearer ${token}` },
                    body: fd,
                  });
                  const data = await res.json();
                  if (!res.ok) throw new Error(data.detail || "Erreur");
                  toast.success(`${data.created} produit${data.created > 1 ? "s" : ""} importé${data.created > 1 ? "s" : ""}.${data.errors?.length ? ` (${data.errors.length} ligne(s) ignorée(s))` : ""}`);
                  if (data.errors?.length) console.warn("CSV import errors:", data.errors);
                  load();
                } catch (err) {
                  toast.error(err.message || "Erreur lors de l'import");
                } finally {
                  setImporting(false);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }
              }}
            />
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Format CSV : colonnes <code>nom, prix, categorie, description, image</code> (header en 1ʳᵉ ligne). Les catégories manquantes sont créées automatiquement.
          </p>

          {/* Barre de recherche + filtre catégorie */}
          <div className="flex flex-wrap gap-3 mb-4 items-center">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                data-testid="products-search"
                placeholder="Rechercher un produit…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-10 rounded-sm"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger data-testid="products-cat-filter" className="w-56 h-10 rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les catégories</SelectItem>
                {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground ml-auto">
              {(() => {
                const q = search.trim().toLowerCase();
                const n = products.filter(p =>
                  (categoryFilter === "all" || p.category_id === categoryFilter) &&
                  (!q || p.name.toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q))
                ).length;
                return `${n} produit${n > 1 ? "s" : ""}`;
              })()}
            </span>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            {(() => {
              const q = search.trim().toLowerCase();
              const todayIso = new Date().toISOString().slice(0, 10);
              const list = products
                .filter(p => categoryFilter === "all" || p.category_id === categoryFilter)
                .filter(p => !q || p.name.toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q));
              if (list.length === 0) {
                return (
                  <div className="col-span-full text-center py-12 text-muted-foreground border border-dashed border-border rounded-sm">
                    Aucun produit ne correspond à votre recherche.
                  </div>
                );
              }
              return list.map((p) => {
                const oosDate = p.out_of_stock_until;
                const isOos = oosDate && oosDate >= todayIso;
                return (
                  <div key={p.id} data-testid={`product-card-${p.id}`} className={`bg-white border rounded-sm p-4 flex gap-4 ${isOos ? "border-amber-400 bg-amber-50/40" : "border-border"}`}>
                    {p.image && <img src={imgUrl(p.image)} alt="" className={`w-16 h-16 object-cover rounded-sm ${isOos ? "opacity-60" : ""}`} />}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between gap-2">
                        <h3 className="font-medium truncate">{p.name}</h3>
                        <span className="text-primary font-semibold shrink-0">{p.price.toFixed(2)} CHF</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {categories.find(c => c.id === p.category_id)?.name} {!p.available && "· INDISPONIBLE"}
                      </p>
                      {isOos && (
                        <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 bg-amber-100 text-amber-900 rounded-sm">
                          <PackageX size={11} /> Rupture jusqu&apos;au {oosDate}
                        </div>
                      )}
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => { setEditing({ ...p }); setEditingType("product"); }} className="text-xs text-primary hover:underline flex items-center gap-1"><Edit size={11} /> Éditer</button>
                        <button onClick={() => del("products", p.id)} className="text-xs text-destructive hover:underline flex items-center gap-1"><Trash2 size={11} /> Suppr.</button>
                        {isOos ? (
                          <button
                            data-testid={`clear-oos-${p.id}`}
                            onClick={async () => {
                              await api.put(`/admin/products/${p.id}`, { ...p, out_of_stock_until: null });
                              toast.success("Remis en stock");
                              load();
                            }}
                            className="text-xs text-emerald-700 hover:underline flex items-center gap-1"
                          >Remettre en stock</button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </TabsContent>

        <TabsContent value="categories" className="mt-6">
          <button onClick={() => { setEditing({ name: "", order: categories.length + 1 }); setEditingType("category"); }} className="bg-primary text-white px-4 py-2 rounded-sm mb-4 flex items-center gap-2 hover:bg-primary/90"><Plus size={16} /> Nouvelle catégorie</button>
          <p className="text-xs text-muted-foreground mb-4">
            Utilisez les flèches pour changer l&apos;ordre d&apos;affichage sur le menu client.
          </p>
          <div className="space-y-2 max-w-2xl">
            {categories.map((c, idx) => (
              <div key={c.id} className="bg-white border border-border rounded-sm p-3 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="flex flex-col">
                    <button
                      disabled={idx === 0}
                      data-testid={`cat-up-${c.id}`}
                      onClick={async () => {
                        const ids = categories.map(x => x.id);
                        [ids[idx - 1], ids[idx]] = [ids[idx], ids[idx - 1]];
                        await api.post("/admin/categories/reorder", { ids });
                        load();
                      }}
                      className="p-0.5 text-muted-foreground hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label="Monter"
                    ><ChevronUp size={16} /></button>
                    <button
                      disabled={idx === categories.length - 1}
                      data-testid={`cat-down-${c.id}`}
                      onClick={async () => {
                        const ids = categories.map(x => x.id);
                        [ids[idx], ids[idx + 1]] = [ids[idx + 1], ids[idx]];
                        await api.post("/admin/categories/reorder", { ids });
                        load();
                      }}
                      className="p-0.5 text-muted-foreground hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label="Descendre"
                    ><ChevronDown size={16} /></button>
                  </div>
                  <span className="text-muted-foreground text-xs">#{idx + 1}</span>
                  <span className="font-medium">{c.name}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setEditing({ ...c }); setEditingType("category"); }} className="text-primary text-sm hover:underline">Éditer</button>
                  <button onClick={() => del("categories", c.id)} className="text-destructive text-sm hover:underline">Suppr.</button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="addons" className="mt-6">
          <button onClick={() => { setEditing({ name: "", price: 0, required: false, multi: true, choices: [] }); setEditingType("addon"); }} className="bg-primary text-white px-4 py-2 rounded-sm mb-4 flex items-center gap-2 hover:bg-primary/90"><Plus size={16} /> Nouveau groupe</button>
          <p className="text-xs text-muted-foreground mb-4">
            Créez un groupe (ex : « Sauce », « Taille »…) avec ses choix. Sans choix, le supplément reste simple (nom + prix unique).
          </p>
          <div className="space-y-2 max-w-2xl">
            {addons.map(a => {
              const hasChoices = (a.choices || []).length > 0;
              return (
                <div key={a.id} className="bg-white border border-border rounded-sm p-3">
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{a.name}</span>
                        {hasChoices && (
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-primary/10 text-primary rounded-sm">
                            {a.multi === false ? "Choix unique" : "Choix multiple"}{a.required ? " · obligatoire" : ""}
                          </span>
                        )}
                      </div>
                      {hasChoices ? (
                        <ul className="mt-2 space-y-0.5 text-sm">
                          {a.choices.map(c => (
                            <li key={c.id} className="flex justify-between text-muted-foreground">
                              <span>• {c.name}</span>
                              <span>+{Number(c.price).toFixed(2)} CHF</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="text-sm text-primary mt-1">+{Number(a.price || 0).toFixed(2)} CHF</div>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => { setEditing({ ...a, choices: [...(a.choices || [])] }); setEditingType("addon"); }} className="text-primary text-sm hover:underline">Éditer</button>
                      <button onClick={() => del("addons", a.id)} className="text-destructive text-sm hover:underline">Suppr.</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing?.id ? "Modifier" : "Ajouter"} {editingType === "product" ? "le produit" : editingType === "category" ? "la catégorie" : "le supplément"}
            </DialogTitle>
          </DialogHeader>
          {editing && editingType === "product" && (
            <div className="space-y-3">
              <Field label="Nom"><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></Field>
              <Field label="Description"><Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={2} /></Field>
              <Field label="Prix (CHF)"><Input type="number" step="0.5" value={editing.price} onChange={(e) => setEditing({ ...editing, price: Number(e.target.value) })} /></Field>
              <Field label="Image du produit">
                <label
                  data-testid="upload-image-label"
                  className="cursor-pointer w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-primary text-primary rounded-sm hover:bg-primary hover:text-white transition font-medium"
                >
                  <Upload size={18} /> Téléverser une image depuis mon ordinateur
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="upload-image-input"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 10 * 1024 * 1024) {
                        toast.error("Image trop lourde (max 10 Mo)");
                        e.target.value = "";
                        return;
                      }
                      try {
                        const fd = new FormData();
                        fd.append("file", file);
                        const token = localStorage.getItem("hana_admin_token");
                        const res = await fetch(`${API}/admin/images/upload`, {
                          method: "POST",
                          headers: { Authorization: `Bearer ${token}` },
                          body: fd,
                        });
                        const data = await res.json();
                        if (!res.ok) throw new Error(data.detail || "Erreur");
                        setEditing((prev) => ({ ...prev, image: data.url }));
                        toast.success("Image téléversée");
                      } catch (err) {
                        toast.error(err.message || "Erreur d'envoi");
                      } finally {
                        e.target.value = "";
                      }
                    }}
                  />
                </label>
                <p className="text-[11px] text-muted-foreground mt-1 mb-3 text-center">JPG · PNG · WEBP · max 10 Mo</p>

                <div className="flex items-center gap-3 my-3">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">ou via une URL</span>
                  <div className="flex-1 h-px bg-border" />
                </div>

                <div className="flex gap-2">
                  <Input value={editing.image} onChange={(e) => setEditing({ ...editing, image: e.target.value })} placeholder="https://… ou /api/uploads/…" />
                  {editing.image && !editing.image.startsWith("/api/uploads/") && (
                    <button
                      type="button"
                      data-testid="host-image-btn"
                      onClick={async () => {
                        try {
                          const { data } = await api.post("/admin/images/from-url", { url: editing.image });
                          setEditing({ ...editing, image: data.url });
                          toast.success("Image hébergée sur Hanamiya");
                        } catch (e) { toast.error(e.response?.data?.detail || "Téléchargement échoué"); }
                      }}
                      className="shrink-0 px-3 bg-primary text-white rounded-sm text-sm hover:bg-primary/90"
                    >Héberger</button>
                  )}
                </div>

                {editing.image && (
                  <div className="mt-3 flex items-center gap-3">
                    <img src={imgUrl(editing.image)} alt="" className="w-24 h-24 object-cover rounded-sm border border-border" />
                    <button
                      type="button"
                      onClick={() => setEditing({ ...editing, image: "" })}
                      className="text-xs text-destructive hover:underline"
                    >Retirer l&apos;image</button>
                  </div>
                )}
              </Field>
              <Field label="Catégorie">
                <Select value={editing.category_id} onValueChange={(v) => setEditing({ ...editing, category_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Copier les addons depuis…">
                <Select value="" onValueChange={(otherId) => {
                  const other = products.find(p => p.id === otherId);
                  if (other) {
                    setEditing({ ...editing, addon_ids: [...(other.addon_ids || [])] });
                    toast.success(`Addons copiés depuis « ${other.name} »`);
                  }
                }}>
                  <SelectTrigger data-testid="copy-addons-from"><SelectValue placeholder="Choisir un produit existant…" /></SelectTrigger>
                  <SelectContent>
                    {products.filter(p => p.id !== editing.id).map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Suppléments disponibles">
                <div className="space-y-1 max-h-40 overflow-y-auto border border-border rounded-sm p-2">
                  {addons.map(a => {
                    const hasChoices = (a.choices || []).length > 0;
                    return (
                      <label key={a.id} className="flex items-center gap-2 text-sm py-1">
                        <input type="checkbox"
                          checked={editing.addon_ids?.includes(a.id) || false}
                          onChange={(e) => {
                            const ids = editing.addon_ids || [];
                            setEditing({ ...editing, addon_ids: e.target.checked ? [...ids, a.id] : ids.filter(x => x !== a.id) });
                          }}
                        />
                        {a.name} {hasChoices ? <span className="text-xs text-muted-foreground">({a.choices.length} choix)</span> : <span className="text-xs text-muted-foreground">(+{Number(a.price || 0).toFixed(2)} CHF)</span>}
                      </label>
                    );
                  })}
                </div>
              </Field>
              <label className="flex items-center gap-3"><Switch checked={editing.available} onCheckedChange={(v) => setEditing({ ...editing, available: v })} /> Disponible (toggle permanent)</label>

              <Field label="Rupture de stock temporaire">
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    type="date"
                    data-testid="product-oos-date"
                    value={editing.out_of_stock_until || ""}
                    min={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setEditing({ ...editing, out_of_stock_until: e.target.value || null })}
                    className="w-48"
                  />
                  {[1, 2, 3, 7].map(n => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => {
                        const d = new Date();
                        d.setDate(d.getDate() + n - 1); // last day of unavailability
                        setEditing({ ...editing, out_of_stock_until: d.toISOString().slice(0, 10) });
                      }}
                      className="text-xs px-2 py-1 border border-border rounded-sm hover:bg-secondary"
                    >{n} jour{n > 1 ? "s" : ""}</button>
                  ))}
                  {editing.out_of_stock_until && (
                    <button
                      type="button"
                      onClick={() => setEditing({ ...editing, out_of_stock_until: null })}
                      className="text-xs text-destructive hover:underline"
                    >Effacer</button>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Le produit sera masqué du menu client jusqu&apos;à la date choisie <strong>incluse</strong>. Il redeviendra visible automatiquement le lendemain.
                </p>
              </Field>
            </div>
          )}
          {editing && editingType === "category" && (
            <div className="space-y-3">
              <Field label="Nom"><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></Field>
              <Field label="Ordre"><Input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></Field>
            </div>
          )}
          {editing && editingType === "addon" && (
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <Field label="Nom du groupe / supplément">
                <Input
                  data-testid="addon-name-input"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="ex : Sauce, Taille, Œuf supplémentaire…"
                />
              </Field>

              <div className="border border-border rounded-sm p-3 space-y-3 bg-secondary/40">
                <div className="flex justify-between items-center">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Choix proposés</Label>
                  <button
                    type="button"
                    data-testid="add-choice-btn"
                    onClick={() => setEditing({ ...editing, choices: [...(editing.choices || []), { name: "", price: 0 }] })}
                    className="text-xs text-primary hover:underline flex items-center gap-1"
                  ><Plus size={12} /> Ajouter un choix</button>
                </div>

                {(editing.choices || []).length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Sans choix ajouté, ce supplément reste un simple toggle avec le prix ci-dessous.
                  </p>
                )}

                {(editing.choices || []).map((c, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Input
                      data-testid={`choice-name-${idx}`}
                      value={c.name}
                      onChange={(e) => {
                        const arr = [...editing.choices];
                        arr[idx] = { ...arr[idx], name: e.target.value };
                        setEditing({ ...editing, choices: arr });
                      }}
                      placeholder="Nom du choix"
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      step="0.5"
                      value={c.price}
                      onChange={(e) => {
                        const arr = [...editing.choices];
                        arr[idx] = { ...arr[idx], price: e.target.value };
                        setEditing({ ...editing, choices: arr });
                      }}
                      placeholder="Prix"
                      className="w-24"
                    />
                    <span className="text-xs text-muted-foreground">CHF</span>
                    <button
                      type="button"
                      onClick={() => {
                        const arr = editing.choices.filter((_, i) => i !== idx);
                        setEditing({ ...editing, choices: arr });
                      }}
                      className="text-destructive hover:bg-destructive/10 p-1 rounded-sm"
                      aria-label="Supprimer ce choix"
                    ><Trash2 size={14} /></button>
                  </div>
                ))}

                {(editing.choices || []).length > 0 && (
                  <div className="space-y-2 pt-2 border-t border-border">
                    <label className="flex items-center gap-3 text-sm">
                      <Switch
                        checked={editing.multi !== false}
                        onCheckedChange={(v) => setEditing({ ...editing, multi: v })}
                      />
                      Choix multiple (sinon : choix unique)
                    </label>
                    <label className="flex items-center gap-3 text-sm">
                      <Switch
                        checked={!!editing.required}
                        onCheckedChange={(v) => setEditing({ ...editing, required: v })}
                      />
                      Sélection obligatoire avant ajout au panier
                    </label>
                  </div>
                )}
              </div>

              {(editing.choices || []).length === 0 && (
                <Field label="Prix simple (CHF)">
                  <Input type="number" step="0.5" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} />
                </Field>
              )}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="px-4 py-2 border border-border rounded-sm">Annuler</button>
            <button onClick={save} data-testid="save-edit-btn" className="px-4 py-2 bg-primary text-white rounded-sm">Enregistrer</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
